// A rich author "user card" shown when the pointer hovers (or keyboard focus
// lands on) a commit's avatar. Like Tooltip it renders through a portal to
// <body> with position:fixed so the narrow, overflow:auto graph column can never
// clip it, and it shows after a short delay. It surfaces as much author detail as
// git records for a commit: the deterministic avatar, the author name and email,
// and when the commit's authored date is known, a relative + absolute timestamp
// and the short hash. Purely informational (no interactive controls) so moving
// the pointer off the avatar simply dismisses it.

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Mail, GitCommit } from 'lucide-react';
import { CommitAvatar } from './CommitAvatar';
import { relativeTime } from './relativeTime';

export interface CommitAuthorHoverProps {
  /** Author display name. */
  name: string;
  /** Author email. */
  email: string;
  /** Authored date of the commit this avatar sits on (unix seconds), when known. */
  date?: number;
  /** Short hash of that commit, shown as subtle context. */
  short?: string;
  /** Delay before showing, in ms. Short by design (native title is ~700ms). */
  delay?: number;
  /** The trigger (the commit avatar). */
  children: ReactNode;
}

interface CardPos {
  left: number;
  top: number;
}

/** Approximate card width used to keep it inside the viewport horizontally. */
const CARD_W = 248;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function CommitAuthorHover({
  name,
  email,
  date,
  short,
  delay = 140,
  children,
}: CommitAuthorHoverProps): ReactElement {
  const [pos, setPos] = useState<CardPos | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cardId = useId();

  const show = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vw = typeof window === 'undefined' ? CARD_W + 16 : window.innerWidth;
      setPos({
        left: clamp(r.left, 8, Math.max(8, vw - CARD_W - 8)),
        top: r.bottom + 6,
      });
    }, delay);
  }, [delay]);

  const hide = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setPos(null);
  }, []);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const label = name.trim() || email.trim() || 'Unknown author';
  const absolute = date !== undefined ? new Date(date * 1000).toLocaleString() : undefined;

  return (
    <span
      ref={triggerRef}
      className="mp-git-authorhover"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      aria-describedby={pos ? cardId : undefined}
    >
      {children}
      {pos &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            id={cardId}
            role="tooltip"
            className="mp-git-authorcard"
            style={{ position: 'fixed', left: pos.left, top: pos.top }}
          >
            <div className="mp-git-authorcard__head">
              <CommitAvatar name={name} email={email} size={36} />
              <div className="mp-git-authorcard__id">
                <span className="mp-git-authorcard__name">{label}</span>
                {email.trim() && (
                  <span className="mp-git-authorcard__email">
                    <Mail size={12} aria-hidden />
                    <span className="mp-git-authorcard__email-text">{email}</span>
                  </span>
                )}
              </div>
            </div>
            {date !== undefined && (
              <div className="mp-git-authorcard__meta" title={absolute}>
                Authored <time dateTime={new Date(date * 1000).toISOString()}>{relativeTime(date)}</time>
                {short && <code className="mp-git-authorcard__hash">{short}</code>}
              </div>
            )}
            {date === undefined && short && (
              <div className="mp-git-authorcard__meta">
                <GitCommit size={12} aria-hidden />
                <code className="mp-git-authorcard__hash">{short}</code>
              </div>
            )}
          </div>,
          document.body,
        )}
    </span>
  );
}
